import { useState, useMemo } from 'react';
import { useCareRequests } from './hooks/useCareRequests';
import type { CareRequest, StatusFilter } from './hooks/useCareRequests';
import ListToolbar from './components/ListToolbar';
import CareRequestTable from './components/CareRequestTable';
import DetailModal from './components/DetailModal';
import { exportCsv } from './utils/exportCsv';

export default function AdminCareListPage() {
  const { data, loading, error, refetch, updateStatus } = useCareRequests();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('전체');
  const [search, setSearch] = useState('');
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);
  const [selectedRow, setSelectedRow] = useState<CareRequest | null>(null);

  const filtered = useMemo(() => {
    let rows = data;
    if (statusFilter !== '전체') {
      rows = rows.filter((r) => r.status === statusFilter);
    }
    if (showDuplicatesOnly) {
      rows = rows.filter((r) => r.is_duplicate);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          r.receipt_number.toLowerCase().includes(q) ||
          r.caregiver_name.toLowerCase().includes(q) ||
          r.caregiver_phone.includes(q) ||
          r.patient_name.toLowerCase().includes(q) ||
          r.patient_phone.includes(q)
      );
    }
    return rows;
  }, [data, statusFilter, search, showDuplicatesOnly]);

  const counts = useMemo(() => ({
    접수: data.filter((r) => r.status === '접수').length,
    진행중: data.filter((r) => r.status === '진행중').length,
    완료: data.filter((r) => r.status === '완료').length,
  }), [data]);

  const duplicateCount = useMemo(
    () => data.filter((r) => r.is_duplicate).length,
    [data]
  );

  const handleStatusChange = async (id: number, status: CareRequest['status']) => {
    return await updateStatus(id, status);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f7faf9' }}>
      {/* Header */}
      <div className="w-full border-b border-gray-100 bg-white sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: '#2E7D6B' }}>
              <i className="ri-dashboard-line text-white text-sm"></i>
            </div>
            <div>
              <p className="text-xs text-gray-400">코리아케어러스 관리자</p>
              <h1 className="text-sm font-bold text-gray-900">신청 목록 관리</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/admin/dashboard"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-gray-600 bg-gray-50 border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-dashboard-line"></i>통계
            </a>
            <a
              href="/admin/match-list"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-gray-600 bg-gray-50 border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-hand-heart-line"></i>매칭내역
            </a>
            <a
              href="/admin/care-request"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              <i className="ri-add-line"></i>신규 접수
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: '전체 접수', value: data.length, icon: 'ri-file-list-3-line', color: '#2E7D6B', bg: '#e8f5f1' },
            { label: '접수', value: counts['접수'], icon: 'ri-time-line', color: '#d97706', bg: '#fef3c7' },
            { label: '진행중', value: counts['진행중'], icon: 'ri-loader-4-line', color: '#2563eb', bg: '#dbeafe' },
            { label: '완료', value: counts['완료'], icon: 'ri-check-double-line', color: '#059669', bg: '#d1fae5' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: s.bg }}>
                <i className={`${s.icon} text-lg`} style={{ color: s.color }}></i>
              </div>
              <div>
                <p className="text-xs text-gray-400">{s.label}</p>
                <p className="text-xl font-bold text-gray-900">{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <ListToolbar
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          search={search}
          onSearchChange={setSearch}
          totalCount={data.length}
          filteredCount={filtered.length}
          onExportCsv={() => exportCsv(filtered)}
          onRefetch={refetch}
          showDuplicatesOnly={showDuplicatesOnly}
          onToggleDuplicates={() => setShowDuplicatesOnly((v) => !v)}
          duplicateCount={duplicateCount}
        />

        {/* Table */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <i className="ri-loader-4-line text-3xl animate-spin mb-3" style={{ color: '#2E7D6B' }}></i>
            <p className="text-sm text-gray-400">데이터를 불러오는 중...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <i className="ri-error-warning-line text-3xl text-red-300 mb-3"></i>
            <p className="text-sm font-semibold text-red-400 mb-1">데이터 로딩 실패</p>
            <p className="text-xs text-gray-400 mb-4">{error}</p>
            <button
              onClick={refetch}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              다시 시도
            </button>
          </div>
        ) : (
          <CareRequestTable
            rows={filtered}
            onStatusChange={handleStatusChange}
            onRowClick={setSelectedRow}
          />
        )}
      </div>

      {selectedRow && (
        <DetailModal row={selectedRow} onClose={() => setSelectedRow(null)} />
      )}
    </div>
  );
}